import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import Accordion from "../components/Accordion";
import Facts from "../components/Facts";
import { useTranslation } from "react-i18next";
import { client, urlFor } from "../lib/sanity";
import "./FastigheterInfo.css";

export default function FastigheterInfo() {
    const { id } = useParams();
    const [fastighet, setFastighet] = useState(null);
    const { t } = useTranslation();

    useEffect(() => {
        client
            .fetch(
                `*[_type == "property" && _id == $id][0]{
          title, address, city, price, livingArea, rooms, description,
          brf, floor, otherFacts, images, bidding, upcoming,
          upcomingViewingDate, floorPlans, form, ovrigt,
          operatingCost[]{ electricity, water, cleaning, amount, comment },
          propertyFacts[]{ label, value },
          documents[]{ asset->{ url, originalFilename } }
        }`,
                { id }
            )
            .then(setFastighet)
            .catch(console.error);
    }, [id]);

    if (!fastighet) return <div className="mag-loading">Laddar...</div>;

    const operatingCosts = Array.isArray(fastighet.operatingCost) ? fastighet.operatingCost : [];
    const heroImage = fastighet.images?.[0];
    const galleryImages = fastighet.images?.slice(1) || [];

    return (
        <article className="mag-prop-wrapper">
            <header className="mag-prop-hero">
                {heroImage && (
                    <div className="mag-prop-hero-img">
                        <img
                            src={urlFor(heroImage).width(2000).url()}
                            alt={fastighet.title}
                            className="mag-hero-main-image"
                        />

                        <div className="mag-hero-overlay">
                            <div className="mag-container">
                                <div className="mag-hero-text-box">
                                    <span className="mag-prop-category">{fastighet.city}</span>
                                    <h1 className="mag-prop-title">{fastighet.address}</h1>

                                    <div className="mag-prop-quick-facts">
                                        {fastighet.rooms && (
                                            <span>{fastighet.rooms} {t("rooms")}</span>
                                        )}

                                        {fastighet.livingArea && (
                                            <>
                                                {fastighet.rooms && <span className="separator">•</span>}
                                                <span>{fastighet.livingArea} m²</span>
                                            </>
                                        )}

                                        {fastighet.price && (
                                            <>
                                                {(fastighet.rooms || fastighet.livingArea) && <span className="separator">•</span>}
                                                <span>{fastighet.price.toLocaleString("sv-SE")} SEK</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mag-prop-status-tags">
                            {fastighet.upcoming && <span className="tag-status">KOMMANDE</span>}
                            {fastighet.sold && <span className="tag-status sold">SÅLD</span>}
                            {fastighet.bidding && <span className="tag-status bidding">BUDGIVNING</span>}
                        </div>
                    </div>
                )}
            </header>

            <div className="mag-container">
                <div className="mag-prop-grid">
                    <main className="mag-prop-main">
                        <section className="mag-prop-section">
                            <div className="mag-prop-description">
                                <PortableText value={fastighet.description} />
                            </div>
                        </section>
                        
                        <section className="mag-prop-section mag-accordion-top">
                            <Accordion
                                sections={[
                                    {
                                        title: "Fakta & Detaljer",
                                        content: fastighet.propertyFacts ? (
                                            <Facts facts={fastighet.propertyFacts} />
                                        ) : (
                                            "Information saknas"
                                        ),
                                    },
                                    {
                                        title: "Driftkostnader",
                                        content: operatingCosts.length > 0 ? (
                                            <div className="mag-operating-costs">
                                                {operatingCosts.map((item, idx) => (
                                                    <div key={idx} className="mag-cost-block">
                                                        <div className="mag-cost-row"><span>El</span> <span>{item.electricity}</span></div>
                                                        <div className="mag-cost-row"><span>Vatten</span> <span>{item.water}</span></div>
                                                        <div className="mag-cost-row"><span>Renhållning</span> <span>{item.cleaning}</span></div>
                                                        <div className="mag-cost-row total"><span>Summa</span> <span>{item.amount}</span></div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : "Information saknas"
                                    },
                                    {
                                        title: "Dokument",
                                        content: fastighet.documents?.length > 0 ? (
                                            <div className="mag-docs-list">
                                                {fastighet.documents.map((doc, idx) => (
                                                    <a key={idx} href={doc.asset.url} target="_blank" rel="noreferrer" className="mag-doc-link">
                                                        <span className="icon">📄</span> {doc.asset.originalFilename || "Dokument"}
                                                    </a>
                                                ))}
                                            </div>
                                        ) : "Inga dokument tillgängliga"
                                    },
                                    {
                                        title: "Övrigt",
                                        content: fastighet.ovrigt ? <PortableText value={fastighet.ovrigt} /> : "Ingen övrig information"
                                    }
                                ]}
                            />
                        </section>

                        <section className="mag-prop-gallery">
                            <span className="mag-small-label">Galleriet</span>
                            <div className="mag-gallery-grid">
                                {galleryImages.map((img, idx) => {
                                    const isSmallPair = (idx % 7 === 5 || idx % 7 === 6);
                                    return (
                                        <figure
                                            key={idx}
                                            className={`mag-prop-figure ${isSmallPair ? 'half-width' : 'full-width'}`}
                                        >
                                            <img
                                                src={urlFor(img).width(1600).url()}
                                                alt=""
                                                loading="lazy"
                                            />
                                        </figure>
                                    );
                                })}
                            </div>
                        </section>
                    </main>

                    <aside className="mag-prop-sidebar">
                        <div className="sticky-sidebar">
                            {fastighet.upcomingViewingDate && (
                                <div className="mag-viewing-card">
                                    <span className="mag-label">Visning</span>
                                    <p className="viewing-date">
                                        {new Date(fastighet.upcomingViewingDate).toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" })}
                                    </p>
                                    <p className="viewing-time">
                                        {new Date(fastighet.upcomingViewingDate).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
                                    </p>
                                </div>
                            )}

                            <div className="mag-agent-card">
                                <span className="mag-label">Ansvarig Mäklare</span>
                                <h3 className="agent-name">Peter Olvsborn</h3>
                                <div className="agent-links">
                                    <a href="tel:0701234567" className="agent-link">070-123 45 67</a>
                                    <a href="mailto:peter@olvsborn.se" className="agent-link">peter@olvsborn.se</a>
                                </div>
                                <button className="mag-btn-secondary" onClick={() => window.location.href = "mailto:peter@olvsborn.se"}>
                                    Kontakta mig
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </article>
    );
}